/**
 * ============================================================================
 * Fast Bundle Cart Drawer Sync Fix (v6.3)
 * ============================================================================
 *
 * Loaded by the theme via snippets/cart-drawer.liquid on every page.
 * Previously lived in Fast Bundle app settings (Custom CSS and JS), but
 * Fast Bundle only injects custom JS on pages with bundle widgets — not on
 * homepages, collection pages, or individual product pages where customers
 * also use the cart drawer.
 *
 * WHAT IT DOES:
 *   Fixes stale pricing in the cart drawer caused by a timing conflict between
 *   Fast Bundle's automatic bundle detection and the Minimog theme's section
 *   rendering. Without this script, the cart drawer briefly shows full prices
 *   before Fast Bundle has recalculated the bundle discounts.
 *
 * HOW IT WORKS:
 *   Patches two Minimog theme methods to schedule a delayed re-render after
 *   Fast Bundle finishes recalculating:
 *
 *   Patch 1 — MCartDrawer.renderContents (add-to-cart)
 *     When a product is added, the theme opens the drawer with pre-discount
 *     HTML. This patch waits for detectBundleDiscounts(), then re-fetches
 *     the drawer sections with corrected pricing.
 *
 *   Patch 2 — MCartDrawerItems.updateQuantity (quantity +/- and item removal)
 *     When quantity is changed or an item is removed via the cart drawer, the
 *     theme calls /cart/change.js and updates the DOM immediately. This patch
 *     triggers the same re-fetch afterward so prices reflect the new tier.
 *
 * DEPENDS ON:
 *   - Minimog theme cart drawer (MCartDrawer, MCartDrawerItems custom elements)
 *   - Fast Bundle's detectBundleDiscounts() global function (gracefully skipped
 *     if not available on the current page)
 *   - Shopify Section Rendering API
 *
 * RELATED THEME FILES:
 *   - assets/cart-drawer.js — MCartDrawer and MCartDrawerItems classes
 *   - assets/cart.js — MCartTemplate base class (updateQuantity method)
 *   - snippets/bundle-progress.liquid — Buy More Save More progress bar
 *   - snippets/cart-drawer.liquid — loads this script; switching logic between
 *     BMS and free-gift bars
 *
 * LAST UPDATED: 2026-07-03
 *
 * CHANGELOG:
 *   v5 (2026-03-31) — Initial dual-patch for renderContents + updateQuantity
 *   v6 (2026-04-01) — Added button disable lock during sync to prevent race
 *     conditions from rapid clicking.
 *   v6.1 (2026-04-01) — Increased Patch 2 delay from 1000ms to 2500ms. Moved
 *     from Fast Bundle app settings into theme assets for global page coverage.
 *     Added console diagnostics for troubleshooting.
 *   v6.2 (2026-04-01) — Reduced Patch 2 delay from 2500ms to 1000ms based on
 *     console timing data showing detectBundleDiscounts completes in ~260ms.
 *     Total click-to-correct drops from ~3.8s to ~2.3s.
 *   v6.3 (2026-04-01) — Controls now locked via CSS class on drawer element
 *     (survives innerHTML swaps). Buttons disabled immediately on qty change,
 *     not after the 1000ms delay. Closes the clickable gap at step 3.
 *   v6.4 (2026-07-03) — Hardened the section re-fetch: requests sections
 *     relative to the CURRENT page path instead of the root URL (a root
 *     request can be hijacked by A/B-testing apps like Shoplift and answered
 *     with a full HTML page), skips the fetch when the section list is empty,
 *     and verifies the response is JSON before parsing. Fixes the
 *     "Unexpected token '<'" SyntaxError seen after add-to-cart.
 *   v6.5 (2026-07-03) — Isolated the detectBundleDiscounts() call in its own
 *     try/catch: the SyntaxError actually originates INSIDE Fast Bundle's
 *     function (their fetch receives HTML), and letting it propagate aborted
 *     our whole section repaint. Now their failure is logged and the repaint
 *     proceeds anyway. Their own duplicate uncaught rejection can only be
 *     fixed by Fast Bundle support.
 *
 * KNOWN ISSUE:
 *   When crossing tier boundaries via +/- (e.g., 4→5 shirts), the drawer may
 *   briefly show a split line item (1 at full price + N at old tier) because
 *   detectBundleDiscounts() mutates the cart mid-render. The server-side cart
 *   is correct — a page refresh resolves the display.
 * ============================================================================
 */
(function() {

  var FB_SYNC_VERSION = 'v6.5';
  console.log('[FB-Sync ' + FB_SYNC_VERSION + '] loaded');

  /**
   * Lock UI controls during sync to prevent double-click race conditions.
   * Disables +/- buttons, remove buttons, and quantity inputs in the cart drawer.
   */
  // Inject a CSS rule that locks controls via a class on the drawer element.
  // This survives innerHTML swaps (unlike per-element disabled attributes).
  var style = document.createElement('style');
  style.textContent =
    '#MinimogCartDrawer.fb-sync-locked .m-quantity__button,' +
    '#MinimogCartDrawer.fb-sync-locked m-cart-remove-button,' +
    '#MinimogCartDrawer.fb-sync-locked .m-quantity__input {' +
    '  pointer-events: none !important;' +
    '  opacity: 0.5 !important;' +
    '}';
  document.head.appendChild(style);

  function setCartDrawerControlsEnabled(enabled) {
    var drawer = document.getElementById('MinimogCartDrawer');
    if (!drawer) return;
    if (enabled) {
      drawer.classList.remove('fb-sync-locked');
    } else {
      drawer.classList.add('fb-sync-locked');
    }
  }

  /**
   * Shared helper: wait for Fast Bundle to recalculate, then re-fetch
   * and swap in corrected section HTML for the cart drawer.
   */
  async function refetchCartDrawerSections(drawer, trigger) {
    var t0 = performance.now();
    console.log('[FB-Sync ' + FB_SYNC_VERSION + '] re-fetch START (trigger: ' + (trigger || 'unknown') + ')');
    try {
      // Disable cart controls to prevent race conditions from rapid clicks
      setCartDrawerControlsEnabled(false);

      // Wait for Fast Bundle to detect and apply bundle discounts
      // (gracefully skipped on pages where Fast Bundle hasn't loaded)
      if (typeof detectBundleDiscounts === 'function') {
        console.log('[FB-Sync] calling detectBundleDiscounts...');
        try {
          await detectBundleDiscounts();
          console.log('[FB-Sync] detectBundleDiscounts done (' + Math.round(performance.now() - t0) + 'ms)');
        } catch (fbErr) {
          // Fast Bundle's own function failed (e.g. its fetch got HTML instead
          // of JSON). Not fatal for us - continue with the section repaint so
          // the drawer still refreshes.
          console.log('[FB-Sync] detectBundleDiscounts failed (' + fbErr + '), continuing with repaint');
        }
      } else {
        console.log('[FB-Sync] detectBundleDiscounts not available on this page, skipping');
      }

      // Small buffer to ensure Shopify has processed the discount update
      await new Promise(function(resolve) { setTimeout(resolve, 300); });

      // Build the list of sections to re-fetch from the drawer's own config
      var sectionEntries = drawer.getSectionsToRender();
      var sectionNames = sectionEntries.map(function(s) {
        return s.section || s.id;
      });
      var uniqueSections = sectionNames.filter(function(val, idx, arr) {
        return val && arr.indexOf(val) === idx;
      });

      // Nothing to re-fetch (e.g. drawer config unavailable) - bail cleanly
      if (uniqueSections.length === 0) {
        console.log('[FB-Sync] no sections to re-fetch, skipping');
        setCartDrawerControlsEnabled(true);
        return;
      }

      // Re-fetch sections with updated bundle pricing. Use the CURRENT page
      // path, not the root URL: root requests can be intercepted by
      // A/B-testing apps (Shoplift) and answered with a full HTML page.
      var sectionsUrl = new URL(window.location.pathname, window.location.origin);
      sectionsUrl.searchParams.set('sections', uniqueSections.join(','));
      var resp = await fetch(sectionsUrl.toString());
      if (!resp.ok) {
        setCartDrawerControlsEnabled(true);
        return;
      }

      // Guard against HTML responses (redirects, interstitials) - parsing
      // them as JSON is what threw the "Unexpected token '<'" SyntaxError
      var contentType = resp.headers.get('content-type') || '';
      if (contentType.indexOf('application/json') === -1) {
        console.log('[FB-Sync] section re-fetch returned non-JSON (' + contentType + '), skipping swap');
        setCartDrawerControlsEnabled(true);
        return;
      }
      var data = await resp.json();

      // Swap in the corrected HTML for each section target
      sectionEntries.forEach(function(s) {
        var sectionKey = s.section || s.id;
        var sectionHtml = data[sectionKey];
        if (!sectionHtml) return;

        var target = s.selector
          ? document.querySelector(s.selector)
          : document.getElementById(s.id);
        if (!target) return;

        var parsed = new DOMParser().parseFromString(sectionHtml, 'text/html');
        var source = s.selector
          ? parsed.querySelector(s.selector)
          : parsed.getElementById(s.id);

        if (source && target) {
          target.innerHTML = source.innerHTML;
        }
      });

      // Log cart state after re-fetch for diagnostics
      try {
        var cartCheck = await fetch(window.Shopify.routes.root + 'cart.json');
        var cartData = await cartCheck.json();
        console.log('[FB-Sync] section re-fetch done (' + Math.round(performance.now() - t0) + 'ms). Cart state: ' +
          cartData.item_count + ' items, $' + (cartData.total_price / 100).toFixed(2) +
          ', lines: ' + cartData.items.map(function(i) { return i.quantity + 'x$' + (i.final_price / 100).toFixed(2); }).join(' + '));
      } catch(e) {}

      // Re-initialize cart drawer UI (quantity buttons, cart note, etc.)
      if (typeof drawer.onCartDrawerUpdate === 'function') {
        drawer.onCartDrawerUpdate();
      }

      // Re-enable cart controls after re-render is complete
      setCartDrawerControlsEnabled(true);
      console.log('[FB-Sync ' + FB_SYNC_VERSION + '] re-fetch COMPLETE (' + Math.round(performance.now() - t0) + 'ms)');
    } catch (err) {
      console.log('[FB-Sync ' + FB_SYNC_VERSION + '] ERROR:', err);
      // Always re-enable controls, even on error
      setCartDrawerControlsEnabled(true);
    }
  }

  function init() {
    var drawer = document.getElementById('MinimogCartDrawer');
    if (!drawer || typeof drawer.renderContents !== 'function') {
      setTimeout(init, 200);
      return;
    }

    // Only patch once (prevents double-patching if Fast Bundle custom JS also loads)
    if (drawer.__fbFixPatched) return;
    drawer.__fbFixPatched = true;
    console.log('[FB-Sync ' + FB_SYNC_VERSION + '] patches applied to MCartDrawer + MCartDrawerItems');

    // --- Patch 1: renderContents (add-to-cart) ---
    var origRenderContents = drawer.renderContents.bind(drawer);

    drawer.renderContents = function(t) {
      // Let the theme do its normal render first (opens the drawer)
      origRenderContents(t);

      // Then schedule a corrected re-render after bundle detection
      console.log('[FB-Sync] Patch 1: add-to-cart, scheduling re-fetch in 500ms');
      setTimeout(function() {
        refetchCartDrawerSections(drawer, 'add-to-cart');
      }, 500);
    };

    // --- Patch 2: updateQuantity on MCartDrawerItems (quantity +/- and removal) ---
    var drawerItems = document.querySelector('m-cart-drawer-items');
    if (drawerItems && typeof drawerItems.updateQuantity === 'function') {
      var origUpdateQuantity = drawerItems.updateQuantity.bind(drawerItems);

      drawerItems.updateQuantity = function(line, quantity, name) {
        // Disable controls immediately to prevent clicks during the gap
        // between the theme's re-render and our sync re-render
        setCartDrawerControlsEnabled(false);

        // Let the theme do its normal /cart/change.js call and DOM update
        origUpdateQuantity(line, quantity, name);

        // Then schedule a corrected re-render after bundle detection
        // 1000ms delay — detectBundleDiscounts() typically completes in ~260ms,
        // but we need the theme's /cart/change.js fetch to finish first
        console.log('[FB-Sync] Patch 2: qty change (line ' + line + ' → ' + quantity + '), scheduling re-fetch in 1000ms');
        setTimeout(function() {
          refetchCartDrawerSections(drawer, 'qty-change');
        }, 1000);
      };
    }
  }

  // Start when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
